import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, Trash2, Upload, Calendar, Filter, HelpCircle } from 'lucide-react';

// Language configuration
const LANGUAGES = {
  french: { code: 'fr', name: 'French', file: '/french_words.txt' },
  spanish: { code: 'es', name: 'Spanish', file: '/spanish_words.txt' },
  portuguese: { code: 'pt-br', name: 'Portuguese (BR)', file: '/portuguese_words.txt' },
  italian: { code: 'it', name: 'Italian', file: '/italian_words.txt' },
  german: { code: 'de', name: 'German', file: '/german_words.txt' },
  russian: { code: 'ru', name: 'Russian', file: '/russian_words.txt' },
  dutch: { code: 'nl', name: 'Dutch' },
};

// Translation templates for "Need help starting?" feature
const HELP_TEMPLATES = {
  french: (word) => `"${word}" veut dire "" en anglais`,
  spanish: (word) => `"${word}" significa "" en inglés`,
  portuguese: (word) => `"${word}" significa "" em inglês`,
  italian: (word) => `"${word}" significa "" in inglese`,
  german: (word) => `"${word}" bedeutet "" auf Englisch`,
  russian: (word) => `"${word}" означает "" по-английски`,
  dutch: (word) => `"${word}" betekent "" in het Engels`,
};

// Level definitions
const LEVELS = ['beginner', 'lower_intermediate', 'upper_intermediate', 'advanced'];

const LEVEL_DISPLAY = {
  'beginner': 'Beginner',
  'lower_intermediate': 'Lower Intermediate',
  'upper_intermediate': 'Upper Intermediate',
  'advanced': 'Advanced',
  'custom': 'User Upload'
};

const LEVEL_COLORS = {
  'beginner': 'bg-green-100 text-green-700 border-green-200',
  'lower_intermediate': 'bg-blue-100 text-blue-700 border-blue-200',
  'upper_intermediate': 'bg-purple-100 text-purple-700 border-purple-200',
  'advanced': 'bg-orange-100 text-orange-700 border-orange-200',
  'custom': 'bg-gray-100 text-gray-700 border-gray-200'
};

// Normalise malformed level strings
const normalizeLevel = (level) => {
  if (!level) return 'custom';
  const normalized = level.toLowerCase().trim();

  if (normalized.includes('beginner') || normalized === 'a1' || normalized === 'a2') return 'beginner';
  if (normalized.includes('lower') && normalized.includes('intermediate')) return 'lower_intermediate';
  if (normalized === 'lower' || normalized === 'b1') return 'lower_intermediate';
  if (normalized.includes('upper') && normalized.includes('intermediate')) return 'upper_intermediate';
  if (normalized === 'upper' || normalized === 'b2') return 'upper_intermediate';
  if (normalized.includes('advanced') || normalized === 'c1' || normalized === 'c2') return 'advanced';

  if (['beginner', 'lower_intermediate', 'upper_intermediate', 'advanced', 'custom'].includes(normalized)) {
    return normalized;
  }

  return 'custom';
};

// Function to get words for each day
async function fetchDailyWords(language, level, date) {
  try {
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    const comboKey = getComboKey(language, level);
    const response = await fetch(`/daily-content/${comboKey}.json`);

    if (!response.ok) {
      throw new Error(`Failed to load daily words for ${comboKey}`);
    }

    const allData = await response.json();

    if (!allData[dateStr]) {
      throw new Error(`No words available for ${dateStr}`);
    }

    return allData[dateStr].words;
  } catch (error) {
    console.error('Error fetching daily words:', error);
    return null;
  }
}

// Helper functions for combo management
const getComboKey = (language, level) => `${language}_${level.toLowerCase()}`;

// FIX: Parse combo key correctly, handling multi-underscore level names
const parseComboKey = (comboKey) => {
  const parts = comboKey.split('_');
  const language = parts[0];
  const level = parts.slice(1).join('_');
  return { language, level };
};

const Toast = ({ message, onClose, onUndo }) => (
  <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
    <div className="bg-indigo-900 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
      <span>{message}</span>
      {onUndo && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUndo();
          }}
          className="bg-white text-indigo-900 px-3 py-1 rounded font-semibold hover:bg-gray-100"
        >
          Undo
        </button>
      )}
      <button onClick={onClose} className="text-white hover:text-gray-300">✕</button>
    </div>
  </div>
);

const LevelBadge = ({ level }) => {
  const normalizedLevel = normalizeLevel(level);
  return (
    <span className={`text-xs px-2 py-1 rounded-full border font-semibold ${LEVEL_COLORS[normalizedLevel]}`}>
      {LEVEL_DISPLAY[normalizedLevel]}
    </span>
  );
};

const Confetti = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 0.5}s`,
            backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181'][Math.floor(Math.random() * 5)]
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          top: -10px;
          animation: confetti-fall 3s linear forwards;
        }
      `}</style>
    </div>
  );
};

const getScoreColor = (score) => {
  if (score === 0) return 'text-red-900 bg-red-50 border-red-200';
  if (score <= 3) return 'text-orange-900 bg-orange-50 border-orange-200';
  if (score <= 6) return 'text-yellow-900 bg-yellow-50 border-yellow-200';
  if (score <= 9) return 'text-green-900 bg-green-50 border-green-200';
  return 'text-yellow-900 bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-400';
};

const getScoreLabel = (score) => {
  if (score === 0) return 'Here\'s a hint';
  if (score <= 3) return 'Keep trying';
  if (score <= 6) return 'Good effort';
  if (score <= 9) return 'Excellent';
  return 'Perfect!';
};

const VocabLearner = () => {
  // Check version and migrate if needed
  useEffect(() => {
    const version = localStorage.getItem('appVersion');
    if (version !== '3.0') {
      if (version === '2.0') {
        // MIGRATION FROM 2.0 TO 3.0
        const oldVocabData = localStorage.getItem('vocabData');
        if (oldVocabData) {
          const oldData = JSON.parse(oldVocabData);
          const newVocabData = {};
          const newReviewData = {};

          Object.keys(oldData).forEach(comboKey => {
            const { language, level } = parseComboKey(comboKey);
            const comboData = oldData[comboKey];

            newVocabData[comboKey] = {
              dailyWords: comboData.dailyWords || [],
              lastRefresh: comboData.lastRefresh || null,
              currentIndex: comboData.currentIndex || 0,
              feedbackLanguage: comboData.feedbackLanguage || 'auto'
            };

            if (!newReviewData[language]) {
              newReviewData[language] = {
                history: [],
                practiceList: [],
                reviewTotalPoints: 0
              };
            }

            if (comboData.history) {
              comboData.history.forEach(entry => {
                newReviewData[language].history.push({
                  ...entry,
                  level: normalizeLevel(entry.level || level)
                });
              });
            }

            if (comboData.practiceList) {
              comboData.practiceList.forEach(word => {
                const exists = newReviewData[language].practiceList.find(w => w.word === word.word);
                if (!exists) {
                  newReviewData[language].practiceList.push({
                    ...word,
                    level: normalizeLevel(word.level || level)
                  });
                }
              });
            }

            newReviewData[language].reviewTotalPoints += comboData.reviewTotalPoints || 0;
          });

          localStorage.setItem('vocabData', JSON.stringify(newVocabData));
          localStorage.setItem('reviewData', JSON.stringify(newReviewData));
        }
        localStorage.setItem('appVersion', '3.0');
      } else {
        localStorage.clear();
        localStorage.setItem('appVersion', '3.0');
      }
      window.location.reload();
    } else {
      // Version 3.0 — run cleanup on existing data to fix any malformed levels
      const reviewData = localStorage.getItem('reviewData');
      if (reviewData) {
        const data = JSON.parse(reviewData);
        let needsUpdate = false;

        Object.keys(data).forEach(language => {
          if (data[language].history) {
            data[language].history = data[language].history.map(entry => {
              const normalized = normalizeLevel(entry.level);
              if (normalized !== entry.level) needsUpdate = true;
              return { ...entry, level: normalized };
            });
          }

          if (data[language].practiceList) {
            data[language].practiceList = data[language].practiceList.map(word => {
              const normalized = normalizeLevel(word.level);
              if (normalized !== word.level) needsUpdate = true;
              return { ...word, level: normalized };
            });
          }
        });

        if (needsUpdate) {
          localStorage.setItem('reviewData', JSON.stringify(data));
          console.log('Fixed malformed level data');
        }
      }
    }
  }, []);

  const [screen, setScreen] = useState('loading');
  const [settings, setSettings] = useState(null);
  const [dailyWords, setDailyWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [examples, setExamples] = useState([]);
  const [userSentence, setUserSentence] = useState('');
  const [userTranslation, setUserTranslation] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [extraExamplesCount, setExtraExamplesCount] = useState(0);
  const [dailyPoints, setDailyPoints] = useState(0);
  const [loadingMoreExamples, setLoadingMoreExamples] = useState(false);
  const [toast, setToast] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const [attemptScores, setAttemptScores] = useState([]);
  const [attemptSentences, setAttemptSentences] = useState([]);
  const [helpButtonUsed, setHelpButtonUsed] = useState(false);

  const [showHaggleInput, setShowHaggleInput] = useState(false);
  const [haggleMessage, setHaggleMessage] = useState('');
  const [haggleLoading, setHaggleLoading] = useState(false);
  const [showHaggleConfirm, setShowHaggleConfirm] = useState(false);
  const [haggleConversation, setHaggleConversation] = useState([]);

  const [practiceList, setPracticeList] = useState([]);
  const [reviewTab, setReviewTab] = useState('practice');
  const [reviewTotalPoints, setReviewTotalPoints] = useState(0);
  const [mainTab, setMainTab] = useState('daily');
  const [reviewWords, setReviewWords] = useState([]);
  const [reviewCurrentIndex, setReviewCurrentIndex] = useState(0);
  const [selectedHistoryWords, setSelectedHistoryWords] = useState(new Set());
  const [levelFilter, setLevelFilter] = useState('all');
  const [practiceListSortOrder, setPracticeListSortOrder] = useState('default');

  const [currentLanguageLevel, setCurrentLanguageLevel] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [availableCombos, setAvailableCombos] = useState([]);

  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const [showSettings, setShowSettings] = useState(false);
  const [tempLanguage, setTempLanguage] = useState(null);
  const [tempLevel, setTempLevel] = useState(null);
  const [tempFeedbackLanguage, setTempFeedbackLanguage] = useState(null);

  const [currentStreak, setCurrentStreak] = useState(0);

  const textareaRef = React.useRef(null);

  const HAGGLE_LIMIT = 3;

  const getHaggleUsedToday = (language) => {
    const haggleData = localStorage.getItem('haggleUsage');
    if (!haggleData) return 0;
    const usage = JSON.parse(haggleData);
    const today = new Date().toDateString();
    if (!usage[language] || usage[language].date !== today) return 0;
    return usage[language].count || 0;
  };

  const canHaggleToday = (language) => getHaggleUsedToday(language) < HAGGLE_LIMIT;

  const getHaggleRemaining = (language) => HAGGLE_LIMIT - getHaggleUsedToday(language);

  const markHaggleUsed = (language) => {
    const haggleData = localStorage.getItem('haggleUsage');
    const usage = haggleData ? JSON.parse(haggleData) : {};
    const today = new Date().toDateString();
    const currentCount = (usage[language]?.date === today) ? (usage[language].count || 0) : 0;
    usage[language] = { date: today, count: currentCount + 1 };
    localStorage.setItem('haggleUsage', JSON.stringify(usage));
  };

  const saveCurrentComboData = () => {
    if (!currentLanguageLevel) return;

    const allVocabData = localStorage.getItem('vocabData');
    const vocabData = allVocabData ? JSON.parse(allVocabData) : {};

    vocabData[currentLanguageLevel] = {
      dailyWords: dailyWords,
      lastRefresh: settings?.lastRefresh || null,
      currentIndex: settings?.currentIndex || 0,
      feedbackLanguage: settings?.feedbackLanguage || 'auto'
    };

    localStorage.setItem('vocabData', JSON.stringify(vocabData));

    if (currentLanguage) {
      const allReviewData = localStorage.getItem('reviewData');
      const reviewData = allReviewData ? JSON.parse(allReviewData) : {};

      reviewData[currentLanguage] = {
        history: history,
        practiceList: practiceList,
        reviewTotalPoints: reviewTotalPoints
      };

      localStorage.setItem('reviewData', JSON.stringify(reviewData));
    }
  };

  // Check streak on app load
  useEffect(() => {
    const streakData = localStorage.getItem('streakData');
    if (streakData) {
      const parsed = JSON.parse(streakData);
      const lastDate = parsed.lastCompletionDate;
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();

      let currentStreakValue = parsed.currentStreak || 0;

      if (lastDate && lastDate !== today && lastDate !== yesterday) {
        currentStreakValue = 0;
        const newStreakData = { ...parsed, currentStreak: 0 };
        localStorage.setItem('streakData', JSON.stringify(newStreakData));
      }

      setCurrentStreak(currentStreakValue);
    }
  }, []);

  // Auto-save when data changes
  useEffect(() => {
    if (currentLanguageLevel && settings && currentLanguage) {
      const allVocabData = localStorage.getItem('vocabData');
      const vocabData = allVocabData ? JSON.parse(allVocabData) : {};

      vocabData[currentLanguageLevel] = {
        dailyWords: dailyWords,
        lastRefresh: settings?.lastRefresh || null,
        currentIndex: settings?.currentIndex || 0,
        feedbackLanguage: settings?.feedbackLanguage || 'auto'
      };

      localStorage.setItem('vocabData', JSON.stringify(vocabData));

      const allReviewData = localStorage.getItem('reviewData');
      const reviewData = allReviewData ? JSON.parse(allReviewData) : {};

      reviewData[currentLanguage] = {
        history: history,
        practiceList: practiceList,
        reviewTotalPoints: reviewTotalPoints
      };

      localStorage.setItem('reviewData', JSON.stringify(reviewData));
    }
  }, [currentLanguageLevel, currentLanguage, history, dailyWords, practiceList, reviewTotalPoints, settings]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      const currentCombo = localStorage.getItem('currentLanguageLevel');
      const allVocabData = localStorage.getItem('vocabData');
      const allReviewData = localStorage.getItem('reviewData');

      if (allVocabData && currentCombo) {
        const vocabData = JSON.parse(allVocabData);
        const reviewData = allReviewData ? JSON.parse(allReviewData) : {};

        // ONE-TIME MIGRATION: backfill examples onto history + practice list entries
        // that were saved before examples were stored on history records.
        if (!localStorage.getItem('examplesMigrationDone')) {
          let migrationChanged = false;
          const dailyContentCache = {};

          const fetchComboData = async (comboKey) => {
            if (dailyContentCache[comboKey] !== undefined) return dailyContentCache[comboKey];
            try {
              const resp = await fetch(`/daily-content/${comboKey}.json`);
              dailyContentCache[comboKey] = resp.ok ? await resp.json() : null;
            } catch (e) {
              dailyContentCache[comboKey] = null;
            }
            return dailyContentCache[comboKey];
          };

          // Parse wordId format: {comboKey}-{YYYY}-{MM}-{DD}-{index}
          // The date is always the last 4 dash-separated segments (year, month, day, index).
          const parseWordId = (wordId) => {
            if (!wordId) return null;
            const parts = wordId.split('-');
            if (parts.length < 5) return null;
            return {
              comboKey: parts.slice(0, parts.length - 4).join('-'),
              dateStr:  parts.slice(parts.length - 4, parts.length - 1).join('-'),
            };
          };

          for (const lang of Object.keys(reviewData)) {
            const langData = reviewData[lang];

            // Backfill history entries
            for (const entry of (langData.history || [])) {
              if (!entry.examples || entry.examples.length === 0) {
                const parsed = parseWordId(entry.wordId);
                if (parsed) {
                  const comboData = await fetchComboData(parsed.comboKey);
                  const dayWords = comboData?.[parsed.dateStr]?.words;
                  if (dayWords) {
                    const match = dayWords.find(w => w.word === entry.word);
                    if (match?.examples?.length) {
                      entry.examples = match.examples;
                      migrationChanged = true;
                    }
                  }
                }
              }
            }

            // Backfill practice list entries — first try history (already updated),
            // then fall back to fetching the daily-content file directly.
            for (const word of (langData.practiceList || [])) {
              if (!word.examples || word.examples.length === 0) {
                // Try history first (free — no fetch needed)
                const histMatch = (langData.history || []).find(
                  h => h.word === word.word && h.examples?.length > 0
                );
                if (histMatch) {
                  word.examples = histMatch.examples;
                  migrationChanged = true;
                } else if (word.id && !word.id.startsWith('custom-')) {
                  const parsed = parseWordId(word.id);
                  if (parsed) {
                    const comboData = await fetchComboData(parsed.comboKey);
                    const dayWords = comboData?.[parsed.dateStr]?.words;
                    if (dayWords) {
                      const match = dayWords.find(w => w.word === word.word);
                      if (match?.examples?.length) {
                        word.examples = match.examples;
                        migrationChanged = true;
                      }
                    }
                  }
                }
              }
            }
          }

          if (migrationChanged) {
            localStorage.setItem('reviewData', JSON.stringify(reviewData));
            console.log('Examples migration: backfilled missing examples from daily-content files.');
          }
          localStorage.setItem('examplesMigrationDone', 'true');
        }

        setCurrentLanguageLevel(currentCombo);
        setAvailableCombos(Object.keys(vocabData));

        // FIX: Use parseComboKey to correctly handle multi-underscore level names
        const { language, level } = parseComboKey(currentCombo);

        setCurrentLanguage(language);

        if (reviewData[language]) {
          setHistory(reviewData[language].history || []);
          setPracticeList(reviewData[language].practiceList || []);
          setReviewTotalPoints(reviewData[language].reviewTotalPoints || 0);
        } else {
          setHistory([]);
          setPracticeList([]);
          setReviewTotalPoints(0);
        }

        const comboData = vocabData[currentCombo];

        const today = new Date().toDateString();
        if (comboData.lastRefresh !== today) {
          const words = await fetchDailyWords(language, level, new Date());

          if (words) {
            setDailyWords(words);
            setSettings({
              language: language,
              level: level,
              dailyWords: words,
              lastRefresh: today,
              currentIndex: 0,
              feedbackLanguage: comboData.feedbackLanguage || 'auto'
            });
          } else {
            setDailyWords(comboData.dailyWords || []);
            setSettings({
              language: language,
              level: level,
              dailyWords: comboData.dailyWords || [],
              lastRefresh: comboData.lastRefresh,
              currentIndex: comboData.currentIndex || 0,
              feedbackLanguage: comboData.feedbackLanguage || 'auto'
            });
          }
        } else {
          setDailyWords(comboData.dailyWords || []);
          setSettings({
            language: language,
            level: level,
            dailyWords: comboData.dailyWords || [],
            lastRefresh: comboData.lastRefresh,
            currentIndex: comboData.currentIndex || 0,
            feedbackLanguage: comboData.feedbackLanguage || 'auto'
          });
        }

        setScreen('home');
      } else {
        setScreen('setup');
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (showSettings && tempLevel) {
      const recommendedLanguage = tempLevel === 'beginner' ? 'english' : 'target';
      setTempFeedbackLanguage(recommendedLanguage);
    }
  }, [tempLevel, showSettings]);

  const totalPoints = history.filter(h => !h.isReview && h.level === settings?.level).reduce((sum, h) => sum + h.points, 0);
  const todayPoints = history
    .filter(h =>
      !h.isReview &&
      h.level === settings?.level &&
      new Date(h.date).toDateString() === new Date().toDateString()
    )
    .reduce((sum, h) => sum + h.points, 0);

  const setupComplete = async (language, level) => {
    setScreen('loading');

    const words = await fetchDailyWords(language, level, new Date());

    if (!words) {
      showToast('Failed to load daily words. Please try again.');
      setScreen('setup');
      return;
    }

    const comboKey = getComboKey(language, level);

    const allVocabData = localStorage.getItem('vocabData');
    const vocabData = allVocabData ? JSON.parse(allVocabData) : {};

    vocabData[comboKey] = {
      dailyWords: words,
      lastRefresh: new Date().toDateString(),
      currentIndex: 0,
      feedbackLanguage: 'auto'
    };

    localStorage.setItem('vocabData', JSON.stringify(vocabData));
    localStorage.setItem('currentLanguageLevel', comboKey);

    const allReviewData = localStorage.getItem('reviewData');
    const reviewData = allReviewData ? JSON.parse(allReviewData) : {};

    if (!reviewData[language]) {
      reviewData[language] = {
        history: [],
        practiceList: [],
        reviewTotalPoints: 0
      };
      localStorage.setItem('reviewData', JSON.stringify(reviewData));
    }

    setCurrentLanguageLevel(comboKey);
    setCurrentLanguage(language);
    setAvailableCombos([comboKey]);
    setHistory(reviewData[language]?.history || []);
    setPracticeList(reviewData[language]?.practiceList || []);
    setReviewTotalPoints(reviewData[language]?.reviewTotalPoints || 0);
    setSettings({
      language,
      level,
      dailyWords: words,
      lastRefresh: new Date().toDateString(),
      currentIndex: 0,
      feedbackLanguage: 'auto'
    });
    setDailyWords(words);
    setScreen('home');
  };

  const switchCombo = async (newLanguage, newLevel, newFeedbackLanguage = 'auto') => {
    const newComboKey = getComboKey(newLanguage, newLevel);

    if (newComboKey === currentLanguageLevel) {
      setSettings(prev => ({ ...prev, feedbackLanguage: newFeedbackLanguage }));
      const allVocabData = localStorage.getItem('vocabData');
      const vocabData = allVocabData ? JSON.parse(allVocabData) : {};
      if (vocabData[newComboKey]) {
        vocabData[newComboKey].feedbackLanguage = newFeedbackLanguage;
        localStorage.setItem('vocabData', JSON.stringify(vocabData));
      }
      return;
    }

    saveCurrentComboData();
    setScreen('loading');

    const allVocabData = localStorage.getItem('vocabData');
    const vocabData = allVocabData ? JSON.parse(allVocabData) : {};
    const allReviewData = localStorage.getItem('reviewData');
    const reviewData = allReviewData ? JSON.parse(allReviewData) : {};

    const comboData = vocabData[newComboKey];

    setCurrentLanguage(newLanguage);
    if (reviewData[newLanguage]) {
      setHistory(reviewData[newLanguage].history || []);
      setPracticeList(reviewData[newLanguage].practiceList || []);
      setReviewTotalPoints(reviewData[newLanguage].reviewTotalPoints || 0);
    } else {
      reviewData[newLanguage] = { history: [], practiceList: [], reviewTotalPoints: 0 };
      localStorage.setItem('reviewData', JSON.stringify(reviewData));
      setHistory([]);
      setPracticeList([]);
      setReviewTotalPoints(0);
    }

    if (comboData) {
      setCurrentLanguageLevel(newComboKey);
      localStorage.setItem('currentLanguageLevel', newComboKey);

      const today = new Date().toDateString();
      if (comboData.lastRefresh !== today) {
        const words = await fetchDailyWords(newLanguage, newLevel, new Date());

        if (words) {
          setDailyWords(words);
          setSettings({
            language: newLanguage,
            level: newLevel,
            dailyWords: words,
            lastRefresh: today,
            currentIndex: 0,
            feedbackLanguage: newFeedbackLanguage === 'auto' && comboData.feedbackLanguage ? comboData.feedbackLanguage : newFeedbackLanguage
          });
        } else {
          setDailyWords(comboData.dailyWords || []);
          setSettings({
            language: newLanguage,
            level: newLevel,
            dailyWords: comboData.dailyWords || [],
            lastRefresh: comboData.lastRefresh,
            currentIndex: comboData.currentIndex || 0,
            feedbackLanguage: newFeedbackLanguage === 'auto' && comboData.feedbackLanguage ? comboData.feedbackLanguage : newFeedbackLanguage
          });
        }
      } else {
        setDailyWords(comboData.dailyWords || []);
        setSettings({
          language: newLanguage,
          level: newLevel,
          dailyWords: comboData.dailyWords || [],
          lastRefresh: comboData.lastRefresh,
          currentIndex: comboData.currentIndex || 0,
          feedbackLanguage: newFeedbackLanguage === 'auto' && comboData.feedbackLanguage ? comboData.feedbackLanguage : newFeedbackLanguage
        });
      }
    } else {
      setCurrentLanguageLevel(newComboKey);
      localStorage.setItem('currentLanguageLevel', newComboKey);

      if (!availableCombos.includes(newComboKey)) {
        setAvailableCombos([...availableCombos, newComboKey]);
      }

      const words = await fetchDailyWords(newLanguage, newLevel, new Date());

      if (!words) {
        showToast('Failed to load daily words. Please try again.');
        setScreen('home');
        return;
      }

      setDailyWords(words);
      setSettings({
        language: newLanguage,
        level: newLevel,
        dailyWords: words,
        lastRefresh: new Date().toDateString(),
        currentIndex: 0,
        feedbackLanguage: newFeedbackLanguage
      });

      vocabData[newComboKey] = {
        dailyWords: words,
        lastRefresh: new Date().toDateString(),
        currentIndex: 0,
        feedbackLanguage: newFeedbackLanguage
      };
      localStorage.setItem('vocabData', JSON.stringify(vocabData));
    }

    setScreen('home');
  };

  const requestMoreExamples = () => {
    const isReviewMode = screen === 'review-practice';
    const currentWord = isReviewMode
      ? reviewWords[reviewCurrentIndex]
      : dailyWords[currentWordIndex];

    // Show 4th example (index 3)
    if (extraExamplesCount === 0 && currentWord.examples && currentWord.examples[3]) {
      setExamples([...examples, currentWord.examples[3]]);
      setExtraExamplesCount(1);
    }
    // Show 5th example (index 4)
    else if (extraExamplesCount === 1 && currentWord.examples && currentWord.examples[4]) {
      setExamples([...examples, currentWord.examples[4]]);
      setExtraExamplesCount(2);
    }
  };

  const startPractice = () => {
    const startIndex = settings.currentIndex || 0;
    const currentWord = dailyWords[startIndex];

    setCurrentWordIndex(startIndex);
    setUserSentence('');
    setUserTranslation('');
    setAttempts(0);
    setFeedback(null);
    setAttemptScores([]);
    setAttemptSentences([]);
    setShowConfetti(false);
    setHelpButtonUsed(false);
    setShowHaggleInput(false);
    setHaggleMessage('');
    setShowHaggleConfirm(false);
    setHaggleConversation([]);

    setExamples(currentWord.examples.slice(0, 3));

    const todayPoints = history
      .filter(h =>
        !h.isReview &&
        h.level === settings.level &&
        new Date(h.date).toDateString() === new Date().toDateString()
      )
      .reduce((sum, h) => sum + h.points, 0);
    setDailyPoints(todayPoints);
    setSubmitAttempted(false);
    setExtraExamplesCount(0);
    setLoadingMoreExamples(false);

    setScreen('practice');
  };

  const handleDontKnow = () => {
    const isReviewMode = screen === 'review-practice';
    const currentWord = isReviewMode
      ? reviewWords[reviewCurrentIndex]
      : dailyWords[currentWordIndex];

    const translation = currentWord.translation || 'Translation not available';

    setAttemptScores([0]);
    setAttemptSentences(['']);
    setAttempts(1);

    setFeedback({
      score: 0,
      message: `"${currentWord.word}" means: ${translation}`,
      translation: null,
      isDontKnow: true,
    });
  };

  const fillHelpTemplate = () => {
    const isReviewMode = screen === 'review-practice';
    const currentWord = isReviewMode
      ? reviewWords[reviewCurrentIndex]
      : dailyWords[currentWordIndex];

    const language = settings?.language;
    const template = HELP_TEMPLATES[language];

    if (template) {
      const text = template(currentWord.word);
      setUserSentence(text);
      setHelpButtonUsed(true);

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const emptyQuotesIndex = text.indexOf('""');
          if (emptyQuotesIndex !== -1) {
            const cursorPosition = emptyQuotesIndex + 1;
            textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 0);
    }
  };

  const submitHaggle = async () => {
    if (!haggleMessage.trim()) return;

    const isReviewMode = screen === 'review-practice';
    const currentWord = isReviewMode
      ? reviewWords[reviewCurrentIndex]
      : dailyWords[currentWordIndex];

    const languageName = LANGUAGES[settings?.language]?.name || 'French';

    setHaggleLoading(true);

    const previousExchanges = haggleConversation.length > 0
      ? `\nPrevious contest exchange:\n${haggleConversation.map(m =>
          `${m.role === 'student' ? 'Student' : 'You'}: ${m.content}`
        ).join('\n\n')}\n`
      : '';

    try {
      const response = await fetch('/.netlify/functions/claude-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `You are re-evaluating a language learner's sentence after they contested their score.

Original word: "${currentWord.word}"
Student's sentence: "${userSentence}"
Your original score: ${feedback.score}/10
Your original feedback: "${feedback.message}"
${previousExchanges}
Student's latest contest message (in ${languageName}): "${haggleMessage}"

Weigh the student's points and decide whether to increase their score. Be slightly lenient and increase by 1-2 points if they make a reasonable argument in ${languageName} that references specific features of their sentence. Do NOT increase the score if:
- The argument is vague (e.g. "my sentence was good") with no specific reference to their sentence
- The contest message is not in ${languageName}
- The original score was already very fair given the rubric
- They are simply repeating a previous argument

NEVER decrease the score.

Format your response as:
SCORE: [new score 0-10, must be >= ${feedback.score}]
FEEDBACK: [1-2 sentences explaining your decision, in ${languageName}]`,
        }),
      });

      const data = await response.json();

      if (data.error) {
        console.error('API error:', data.error);
        showToast('Error processing your appeal. Please try again.');
      } else {
        const responseText = data.response.trim();
        const scoreMatch = responseText.match(/SCORE:\s*(\d+)/i);
        const feedbackMatch = responseText.match(/FEEDBACK:\s*(.+)/is);

        const newScore = scoreMatch ? parseInt(scoreMatch[1]) : feedback.score;
        const newMessage = feedbackMatch ? feedbackMatch[1].trim() : responseText;

        setHaggleConversation(prev => [
          ...prev,
          { role: 'student', content: haggleMessage },
          { role: 'assistant', content: responseText }
        ]);

        if (newScore > feedback.score) {
          setFeedback({ ...feedback, score: newScore, message: newMessage });

          const updatedScores = [...attemptScores];
          updatedScores[updatedScores.length - 1] = newScore;
          setAttemptScores(updatedScores);

          showToast(`Score increased to ${newScore}/10! 🎉`);
        } else {
          setFeedback({ ...feedback, message: newMessage });
          showToast('Score unchanged.');
        }

        markHaggleUsed(settings.language);
        setShowHaggleInput(false);
        setHaggleMessage('');
      }
    } catch (error) {
      console.error('Error submitting haggle:', error);
      showToast('Error processing your appeal. Please try again.');
    }

    setHaggleLoading(false);
  };

  const handleHaggleClick = () => {
    setShowHaggleConfirm(true);
  };

  const submitSentence = async () => {
    const isReviewMode = screen === 'review-practice';
    const currentWord = isReviewMode
      ? reviewWords[reviewCurrentIndex]
      : dailyWords[currentWordIndex];

    setSubmitAttempted(true);

    if (userSentence.trim().length < currentWord.word.length + 5) {
      return;
    }

    const languageName = LANGUAGES[settings?.language]?.name || 'French';
    const userLevel = settings?.level || 'A1';
    const feedbackPreference = settings?.feedbackLanguage || 'auto';

    let feedbackLanguage = 'English';

    if (feedbackPreference === 'target') {
      feedbackLanguage = languageName;
    } else if (feedbackPreference === 'english') {
      feedbackLanguage = 'English';
    } else {
      feedbackLanguage = (userLevel === 'beginner') ? 'English' : languageName;
    }

    setLoading(true);

    const visibleExamples = examples.slice(0, 3 + extraExamplesCount);

    try {
      const response = await fetch('/.netlify/functions/claude-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `You are an expert language coach evaluating a ${userLevel} student's ${languageName} sentence using the word "${currentWord.word}".

Sentence: "${userSentence}"

Example sentences shown to student (CHECK FOR PLAGIARISM):
${visibleExamples.map((ex, i) => `${i + 1}. ${ex}`).join('\n')}

### SCORING RUBRIC (0-10) - STRICTLY APPLY THESE DEFINITIONS:

**NO EFFORT / CHEATING (0)**
0: English used, irrelevant nonsense 

**MINIMAL EFFORT (1-3)**
1-3: "Lazy" sentences.
   - Exact copy-paste of an example above.
   - Fill-in-the-blank templates (e.g., "I like [word]", "It is [word]").
   - 3-word sentences that are technically correct but show zero thought.
   - Significant grammar errors that make it hard to understand.

**GENUINE ATTEMPT (4-6)**
4-6: The "Safe" Zone. 
   - A standard, functional but unremarkable sentence.
   - Correct grammar but not much attention paid to contextual specificity or similar.
   - OR: A more ambitious sentence with several grammar mistakes.

**NATIVE-LIKE (7-8)**
7-8: The "Good" Zone.
   - Sounds like something a native speaker would actually say.
   - Natural flow, good context.

**EXCELLENT (9)**
9: Excellent & Nuanced.
   - Near-flawless grammar AND sounds authentic and natural.
   - Uses complex structure and/or specific vocabulary correctly.

**BRILLIANT (10)**
10: Clear masterful use.
   - Perfect grammar + explicit creativity/novelty.
   - Uses the word in a witty, idiomatic, or emotionally descriptive way whilst maintaining correctness. 

### CRITICAL RULES FOR ${userLevel}:
1. **QUALITY OVER QUANTITY:** Do NOT reward long, rambling sentences. A short, perfectly precise 5-word sentence (e.g., an idiom or sharp retort) can score a 10.
2. **LEVEL ADJUSTMENT:** - For **Beginner**: "The cat is on the table" is a **6** (Solid/Correct). To get a 9/10, they must add a *reason* or *feeling* ("...because he is hungry").
   - For **Advanced**: "The cat is on the table" is a **2 or 3** (Too basic). To get a 9/10, they must use nuance and specific vocab ("The cat perched agitatedly...").

Format (in ${feedbackLanguage}):
SCORE: [0-10]
FEEDBACK: [1-2 short sentences only]`,
        }),
      });

      const data = await response.json();

      if (data.error) {
        console.error('API error:', data.error);
        setFeedback({ isError: true, message: 'Error checking sentence. Please try again.' });
      } else {
        const responseText = data.response.trim();
        const scoreMatch = responseText.match(/SCORE:\s*(\d+)/i);
        const feedbackMatch = responseText.match(/FEEDBACK:\s*(.+)/is);

        const score = scoreMatch ? parseInt(scoreMatch[1]) : 0;
        const message = feedbackMatch ? feedbackMatch[1].trim() : responseText;

        const newAttemptScores = [...attemptScores, score];
        const newAttemptSentences = [...attemptSentences, userSentence];

        setAttemptScores(newAttemptScores);
        setAttemptSentences(newAttemptSentences);
        setAttempts(newAttemptScores.length);

        if (score === 10) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }

        const translation = currentWord.translation || 'Translation not available';

        setFeedback({ score, message, translation });
      }
    } catch (error) {
      console.error('Error checking sentence:', error);
      setFeedback({ isError: true, message: 'Error checking sentence. Please try again.' });
    }

    setLoading(false);
  };

  const completeWord = () => {
    const currentWord = dailyWords[currentWordIndex];
    const bestScore = attemptScores.length > 0 ? Math.max(...attemptScores) : 0;

    const wordRecord = {
      wordId: currentWord.id,
      word: currentWord.word,
      level: settings.level,
      examples: currentWord.examples || [],  // FIX: store pre-generated examples for later use in review
      attemptScores: attemptScores,
      attemptSentences: attemptSentences,
      points: bestScore,
      attempts: attemptScores.length,
      userSentence: attemptSentences[attemptScores.indexOf(bestScore)] || userSentence,
      translation: userTranslation,
      date: new Date().toISOString(),
      known: bestScore >= 7,
      isDontKnow: feedback?.isDontKnow || false,
    };

    const newHistory = [...history, wordRecord];
    setHistory(newHistory);

    setDailyPoints(prev => prev + bestScore);

    if (currentWordIndex < dailyWords.length - 1) {
      const nextIndex = currentWordIndex + 1;
      const nextWord = dailyWords[nextIndex];

      setExamples(nextWord.examples.slice(0, 3));

      setCurrentWordIndex(nextIndex);
      setUserSentence('');
      setUserTranslation('');
      setAttempts(0);
      setFeedback(null);
      setAttemptScores([]);
      setAttemptSentences([]);
      setShowConfetti(false);
      setSubmitAttempted(false);
      setExtraExamplesCount(0);
      setLoadingMoreExamples(false);
      setHelpButtonUsed(false);
      setShowHaggleInput(false);
      setHaggleMessage('');
      setShowHaggleConfirm(false);
      setHaggleConversation([]);

      const updatedSettings = { ...settings, currentIndex: nextIndex };
      setSettings(updatedSettings);
    } else {
      // Completed all words — update streak
      const streakData = localStorage.getItem('streakData');
      let current = 0;
      let longest = 0;
      let lastDate = null;

      if (streakData) {
        const parsed = JSON.parse(streakData);
        current = parsed.currentStreak || 0;
        longest = parsed.longestStreak || 0;
        lastDate = parsed.lastCompletionDate;
      }

      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();

      if (lastDate === today) {
        // Already completed today
      } else if (lastDate === yesterday) {
        current++;
        if (current > longest) longest = current;
      } else {
        current = 1;
        if (longest === 0) longest = 1;
      }

      const newStreakData = { currentStreak: current, longestStreak: longest, lastCompletionDate: today };
      localStorage.setItem('streakData', JSON.stringify(newStreakData));
      setCurrentStreak(current);

      setScreen('complete');
    }
  };

  const savePracticeList = (list) => {
    setPracticeList(list);
  };

  const removeFromPracticeList = (wordToRemove) => {
    const newList = practiceList.filter(w => w.word !== wordToRemove.word);
    savePracticeList(newList);
  };

  const toggleStar = (wordToToggle) => {
    const newList = practiceList.map(w =>
      w.word === wordToToggle.word ? { ...w, starred: !w.starred } : w
    );
    savePracticeList(newList);
  };

  // FIX: groupHistoryByDate now uses ISO date strings as keys so sorting is reliable
  const groupHistoryByDate = () => {
    const grouped = {};
    const filtered = levelFilter === 'all'
      ? history.filter(h => !h.isReview)
      : history.filter(h => !h.isReview && h.level === levelFilter);

    filtered.forEach(h => {
      const isoDate = new Date(h.date).toISOString().split('T')[0]; // YYYY-MM-DD — sorts correctly
      const displayDate = new Date(h.date).toLocaleDateString();
      if (!grouped[isoDate]) {
        grouped[isoDate] = { displayDate, words: [] };
      }
      grouped[isoDate].words.push(h);
    });

    return Object.entries(grouped)
      .sort(([a], [b]) => new Date(b) - new Date(a)) // reverse chronological
      .map(([, { displayDate, words }]) => [displayDate, words]);
  };

  const toggleHistoryWord = (wordId) => {
    const newSet = new Set(selectedHistoryWords);
    if (newSet.has(wordId)) {
      newSet.delete(wordId);
    } else {
      newSet.add(wordId);
    }
    setSelectedHistoryWords(newSet);
  };

  // FIX: generateExamplesForCustomWord now generates 5 examples and stores them on the word
  // so requestMoreExamples works as normal
  const generateExamplesForCustomWord = async (word) => {
    setLoading(true);
    const languageName = LANGUAGES[settings?.language]?.name || 'French';

    let complexityInstruction = '';
    const userLevel = settings?.level || 'beginner';
    if (userLevel === 'beginner') {
      complexityInstruction = 'Use only simple, beginner-friendly language and basic vocabulary. ';
    } else if (userLevel === 'lower_intermediate') {
      complexityInstruction = 'Use intermediate-level language only. ';
    }

    try {
      const response = await fetch('/.netlify/functions/claude-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `You are a language education tool. Generate 5 example sentences in ${languageName} using the word "${word.word}". 
                  ${complexityInstruction}This is for educational vocabulary learning - all words in the language, including colloquial or vulgar terms, should be taught with appropriate example usage.
                  Each sentence should be natural, conversational, and demonstrate different contexts.
                  Format: Just return 5 sentences, one per line, nothing else.`,
        }),
      });

      const data = await response.json();

      if (data.error) {
        console.error('API error:', data.error);
        const fallback = [
          "Example 1 (failed to load)",
          "Example 2 (failed to load)",
          "Example 3 (failed to load)",
          "Example 4 (failed to load)",
          "Example 5 (failed to load)"
        ];
        // Still attach fallback examples so requestMoreExamples doesn't break
        setReviewWords(prev => prev.map(w =>
          w.id === word.id ? { ...w, examples: fallback } : w
        ));
        setExamples(fallback.slice(0, 3));
      } else {
        const allExamples = data.response.split('\n').filter(s => s.trim()).slice(0, 5);
        // FIX: Attach all 5 examples to the word object in reviewWords
        setReviewWords(prev => prev.map(w =>
          w.id === word.id ? { ...w, examples: allExamples } : w
        ));
        setExamples(allExamples.slice(0, 3));
      }
    } catch (error) {
      console.error('Error generating examples:', error);
      const fallback = [
        "Example 1 (failed to load)",
        "Example 2 (failed to load)",
        "Example 3 (failed to load)",
        "Example 4 (failed to load)",
        "Example 5 (failed to load)"
      ];
      setReviewWords(prev => prev.map(w =>
        w.id === word.id ? { ...w, examples: fallback } : w
      ));
      setExamples(fallback.slice(0, 3));
    }
    setLoading(false);
  };

  const addSelectedWordsToPracticeList = () => {
    const wordsToAdd = [];
    selectedHistoryWords.forEach(wordId => {
      const historyEntry = history.find(h => h.wordId === wordId);
      if (historyEntry) {
        const fullWord = dailyWords.find(w => w.word === historyEntry.word);

        let word;
        if (fullWord) {
          word = {
            ...fullWord,
            starred: false,
            level: historyEntry.level,
          };
        } else {
          word = {
            id: historyEntry.wordId,
            word: historyEntry.word,
            level: historyEntry.level || 'custom',
            // FIX: carry over pre-generated examples stored in history entry
            examples: historyEntry.examples || [],
            starred: false
          };
        }

        if (!practiceList.find(w => w.word === word.word)) {
          wordsToAdd.push(word);
        }
      }
    });

    if (wordsToAdd.length > 0) {
      const newList = [...practiceList, ...wordsToAdd];
      savePracticeList(newList);
    }

    setSelectedHistoryWords(new Set());
    showToast(`Added ${wordsToAdd.length} word${wordsToAdd.length === 1 ? '' : 's'} to practice list!`);
  };

  const addDayToPracticeList = (dayWords) => {
    const wordsToAdd = [];
    dayWords.forEach(h => {
      const fullWord = dailyWords.find(w => w.word === h.word);

      let word;
      if (fullWord) {
        word = {
          ...fullWord,
          starred: false,
          level: h.level,
        };
      } else {
        word = {
          id: h.wordId,
          word: h.word,
          level: h.level || 'custom',
          // FIX: carry over pre-generated examples stored in history entry
          examples: h.examples || [],
          starred: false
        };
      }

      if (!practiceList.find(w => w.word === word.word)) {
        wordsToAdd.push(word);
      }
    });

    if (wordsToAdd.length > 0) {
      const newList = [...practiceList, ...wordsToAdd];
      savePracticeList(newList);
    }

    showToast(`Added ${wordsToAdd.length} word${wordsToAdd.length === 1 ? '' : 's'} to practice list!`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split('\n');
      const newWords = lines
        .filter(line => line.trim())
        .map((line, i) => ({
          id: `custom-${Date.now()}-${i}`,
          word: line.trim(),
          rank: 999999,
          level: 'custom',
          examples: [],  // Will be generated on review start
          starred: false,
        }));

      const combined = [...practiceList, ...newWords];
      const unique = combined.filter((w, i, arr) =>
        arr.findIndex(x => x.word === w.word) === i
      );

      savePracticeList(unique);
      showToast(`Added ${newWords.length} word${newWords.length === 1 ? '' : 's'}!`);
    };
    reader.readAsText(file);
  };

  const startReview = async () => {
    let pool = [...practiceList];

    if (pool.length === 0) {
      showToast('No words in practice list!');
      return;
    }

    const weightedPool = [];
    pool.forEach(w => {
      weightedPool.push(w);
      if (w.starred) {
        weightedPool.push(w, w);
      }
    });

    const shuffled = [...weightedPool].sort(() => Math.random() - 0.5);
    const selected = [];
    const seen = new Set();

    for (const word of shuffled) {
      if (!seen.has(word.word) && selected.length < 10) {
        selected.push(word);
        seen.add(word.word);
      }
    }

    setReviewWords(selected);
    setReviewCurrentIndex(0);
    setCurrentWordIndex(0);
    setUserSentence('');
    setUserTranslation('');
    setAttempts(0);
    setFeedback(null);
    setAttemptScores([]);
    setAttemptSentences([]);
    setShowConfetti(false);
    setSubmitAttempted(false);
    setExtraExamplesCount(0);
    setLoadingMoreExamples(false);
    setHelpButtonUsed(false);
    setShowHaggleInput(false);
    setHaggleMessage('');
    setShowHaggleConfirm(false);
    setHaggleConversation([]);
    setScreen('loading');

    const firstWord = selected[0];
    // FIX: check for any pre-generated examples (not strictly length === 5)
    if (firstWord.examples && firstWord.examples.length > 0) {
      setExamples(firstWord.examples.slice(0, 3));
      setScreen('review-practice');
    } else {
      // Custom uploaded word — generate examples
      await generateExamplesForCustomWord(firstWord);
      setScreen('review-practice');
    }
  };

  const completeReviewWord = async () => {
    const word = reviewWords[reviewCurrentIndex];
    const bestScore = attemptScores.length > 0 ? Math.max(...attemptScores) : 0;

    const wordRecord = {
      wordId: word.id,
      word: word.word,
      level: word.level || 'custom',
      examples: word.examples || [],
      attemptScores: attemptScores,
      attemptSentences: attemptSentences,
      points: bestScore,
      attempts: attemptScores.length,
      userSentence: attemptSentences[attemptScores.indexOf(bestScore)] || userSentence,
      translation: userTranslation,
      date: new Date().toISOString(),
      known: bestScore >= 7,
      isReview: true,
      isDontKnow: feedback?.isDontKnow || false,
    };

    const newHistory = [...history, wordRecord];
    setHistory(newHistory);

    const newReviewPoints = reviewTotalPoints + bestScore;
    setReviewTotalPoints(newReviewPoints);

    if (reviewCurrentIndex < reviewWords.length - 1) {
      const nextIndex = reviewCurrentIndex + 1;
      const nextWord = reviewWords[nextIndex];

      setLoading(true);

      // FIX: check for any pre-generated examples
      if (nextWord.examples && nextWord.examples.length > 0) {
        setExamples(nextWord.examples.slice(0, 3));
        setLoading(false);
      } else {
        await generateExamplesForCustomWord(nextWord);
      }

      setReviewCurrentIndex(nextIndex);
      setCurrentWordIndex(nextIndex);
      setUserSentence('');
      setUserTranslation('');
      setAttempts(0);
      setFeedback(null);
      setAttemptScores([]);
      setAttemptSentences([]);
      setShowConfetti(false);
      setSubmitAttempted(false);
      setExtraExamplesCount(0);
      setLoadingMoreExamples(false);
      setHelpButtonUsed(false);
      setShowHaggleInput(false);
      setHaggleMessage('');
      setShowHaggleConfirm(false);
      setHaggleConversation([]);
    } else {
      setScreen('review-complete');
    }
  };

  const showToast = (message, onUndo = null) => {
    setToast({ message, onUndo });
    setTimeout(() => setToast(null), onUndo ? 5000 : 3000);
  };

  const ConfirmModal = ({ message, onConfirm, onCancel }) => (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCancel} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
          <p className="text-gray-800 mb-6">{message}</p>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300">
              Cancel
            </button>
            <button onClick={onConfirm} className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700">
              Exit
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const HaggleConfirmModal = ({ onConfirm, onCancel, languageName, remaining }) => (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onCancel} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-5 max-w-sm w-full border-2 border-indigo-200">
          <p className="text-gray-800 mb-4 text-sm">
            <strong>Contest this score?</strong>
            <br />
            You can contest up to <strong>3 times per day</strong>.
            <br />
            You have <strong>{remaining} contest{remaining === 1 ? '' : 's'} remaining</strong> today.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700 text-sm"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const getAvailableLevels = () => {
    const levels = new Set(history.map(h => h.level));
    return Array.from(levels).sort((a, b) => {
      const order = ['beginner', 'lower_intermediate', 'upper_intermediate', 'advanced', 'custom'];
      return order.indexOf(a) - order.indexOf(b);
    });
  };

  const getFilteredPracticeList = () => {
    let filtered = levelFilter === 'all' ? practiceList : practiceList.filter(w => w.level === levelFilter);

    if (practiceListSortOrder === 'score') {
      filtered = [...filtered].sort((a, b) => {
        const aHistory = history.filter(h => h.word === a.word);
        const bHistory = history.filter(h => h.word === b.word);
        const aScore = aHistory.length > 0 ? Math.max(...aHistory.map(h => h.points)) : 0;
        const bScore = bHistory.length > 0 ? Math.max(...bHistory.map(h => h.points)) : 0;
        return aScore - bScore;
      });
    }

    return filtered;
  };

  const clearAllPracticeWords = () => {
    const backup = [...practiceList];
    setPracticeList([]);

    showToast('Practice list cleared', () => {
      setPracticeList(backup);
      setToast(null);
    });
  };

  // RENDER SCREENS

  if (screen === 'loading') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-indigo-900 mb-4">Loading...</div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'setup') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-2xl p-8 md:p-12 max-w-2xl w-full">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <img src="/logo.svg" alt="SqueakEasy Logo" className="w-12 h-12 md:w-16 md:h-16" />
                <h1 className="text-4xl md:text-5xl font-bold text-indigo-900">SqueakEasy</h1>
              </div>
              <p className="text-gray-600 text-lg">Learn new vocab every day!</p>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Select your language:</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(LANGUAGES).map(([key, lang]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedLanguage(key)}
                    className={`p-4 border-2 rounded-xl transition font-semibold ${
                      selectedLanguage === key
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                        : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            {selectedLanguage && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Select your current level:</h2>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-gray-700">
                    Choose the level that best matches your current ability. You can change this anytime in settings!
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {LEVELS.map(level => (
                    <button
                      key={level}
                      onClick={() => setSelectedLevel(level)}
                      className={`p-4 border-2 rounded-xl transition font-semibold ${
                        selectedLevel === level
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                          : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                      }`}
                    >
                      {LEVEL_DISPLAY[level]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedLanguage && selectedLevel && (
              <button
                onClick={() => setupComplete(selectedLanguage, selectedLevel)}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition"
              >
                Start Learning
              </button>
            )}

            <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-100 mt-6">
              <h3 className="font-semibold text-indigo-900 mb-2">How it works:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Learn 10 new words every day at your level</li>
                <li>• Write example sentences using each word</li>
                <li>• Receive instant AI feedback on your usage</li>
                <li>• Earn points: more sophisticated, natural-sounding sentences score higher</li>
                <li>• You get three attempts to get as high a score as possible!</li>
              </ul>
            </div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'home') {
    const dailyWordIds = new Set(dailyWords.map(w => w.id));
    const completedToday = history.filter(h =>
      !h.isReview &&
      dailyWordIds.has(h.wordId) &&
      h.level === settings.level &&
      new Date(h.date).toDateString() === new Date().toDateString()
    ).length;

    const availableLevels = getAvailableLevels();
    const filteredPracticeList = getFilteredPracticeList();

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-2 mb-6 flex gap-2">
              <button
                onClick={() => setMainTab('daily')}
                className={`flex-1 py-3 rounded-lg font-semibold transition ${
                  mainTab === 'daily' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Daily Practice
              </button>
              <button
                onClick={() => setMainTab('review')}
                className={`flex-1 py-3 rounded-lg font-semibold transition ${
                  mainTab === 'review' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Review Centre
              </button>
            </div>

            {mainTab === 'daily' && (
              <div>
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
                  <div className="flex items-center gap-4 mb-8">
                    <img src="/logo.svg" alt="SqueakEasy Logo" className="w-16 h-16 md:w-20 md:h-20" />
                    <h1 className="text-3xl md:text-4xl font-bold text-indigo-900">SqueakEasy</h1>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-indigo-50 p-5 rounded-xl border border-indigo-100">
                      <div className="text-sm text-indigo-600 font-semibold mb-1">Total Score</div>
                      <div className="text-3xl font-bold text-indigo-900">{totalPoints}</div>
                    </div>
                    <div className="bg-green-50 p-5 rounded-xl border border-green-100">
                      <div className="text-sm text-green-600 font-semibold mb-1">Today's Score</div>
                      <div className="text-3xl font-bold text-green-900">{todayPoints}</div>
                    </div>
                    <div className="bg-purple-50 p-5 rounded-xl border border-purple-100">
                      <div className="text-sm text-purple-600 font-semibold mb-1">Words Mastered</div>
                      <div className="text-3xl font-bold text-purple-900">
                        {history.filter(h => h.known).length}
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                      <h2 className="text-lg font-semibold text-gray-700">Today's Progress</h2>
                      <span className="text-sm font-medium text-gray-600">{completedToday}/10 words</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-indigo-600 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${(completedToday / 10) * 100}%` }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={startPractice}
                    className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    disabled={completedToday >= 10}
                  >
                    {completedToday >= 10 ? (
                      '🎉 Daily Challenge Complete!'
                    ) : completedToday > 0 ? (
                      <>Continue Challenge <ArrowRight size={22} /></>
                    ) : (
                      <>Start Today's Challenge <ArrowRight size={22} /></>
                    )}
                  </button>
                </div>

                {history.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Recent History{settings?.level ? ` (${LEVEL_DISPLAY[settings.level] || settings.level})` : ''}
                    </h2>
                    <div className="space-y-2">
                      {history.filter(h => !h.isReview && h.level === settings.level).slice(-5).reverse().map((h, i) => (
                        <div key={i} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800">{h.word}</span>
                            <LevelBadge level={h.level} />
                          </div>
                          <div className="text-indigo-600 font-bold">{h.points}/10</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {mainTab === 'review' && (
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                <h1 className="text-3xl font-bold text-indigo-900 mb-6">Review Centre</h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-amber-50 p-5 rounded-xl border border-amber-100">
                    <div className="text-sm text-amber-600 font-semibold mb-1">Review Score</div>
                    <div className="text-3xl font-bold text-amber-900">{reviewTotalPoints}</div>
                  </div>
                  <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
                    <div className="text-sm text-blue-600 font-semibold mb-1">Practice List</div>
                    <div className="text-3xl font-bold text-blue-900">{practiceList.length}</div>
                  </div>
                </div>

                <div className="flex gap-2 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setReviewTab('practice')}
                    className={`px-4 py-2 font-semibold transition ${
                      reviewTab === 'practice' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Practice List
                  </button>
                  <button
                    onClick={() => setReviewTab('history')}
                    className={`px-4 py-2 font-semibold transition ${
                      reviewTab === 'history' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    History
                  </button>
                  <button
                    onClick={() => setReviewTab('upload')}
                    className={`px-4 py-2 font-semibold transition ${
                      reviewTab === 'upload' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Upload
                  </button>
                </div>

                {reviewTab === 'practice' && (
                  <div>
                    {practiceList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={clearAllPracticeWords}
                              className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition font-medium text-sm"
                            >
                              Clear All
                            </button>
                            <select
                              value={practiceListSortOrder}
                              onChange={(e) => setPracticeListSortOrder(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-sm"
                            >
                              <option value="default">Default Order</option>
                              <option value="score">Sort by Score</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-3">
                            <Filter size={18} className="text-gray-600" />
                            <select
                              value={levelFilter}
                              onChange={(e) => setLevelFilter(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                            >
                              <option value="all">All Levels</option>
                              {availableLevels.map(level => (
                                <option key={level} value={level}>{LEVEL_DISPLAY[level]}</option>
                              ))}
                            </select>
                            <span className="text-sm text-gray-600">
                              {filteredPracticeList.length} word{filteredPracticeList.length === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {filteredPracticeList.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p className="mb-2">
                          {levelFilter === 'all'
                            ? 'Your practice list is empty!'
                            : `No ${LEVEL_DISPLAY[levelFilter]} words in practice list.`}
                        </p>
                        <p className="text-sm">
                          {levelFilter === 'all'
                            ? 'Add words from History or Upload your own.'
                            : 'Try a different filter or add more words.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
                        {filteredPracticeList.map((word, i) => {
                          const wordHistory = history.filter(h => h.word === word.word);
                          const bestScore = wordHistory.length > 0 ? Math.max(...wordHistory.map(h => h.points)) : null;

                          return (
                            <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition gap-3">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-semibold text-gray-800 truncate">{word.word}</span>
                                <LevelBadge level={word.level} />
                                {practiceListSortOrder === 'score' && bestScore !== null && (
                                  <span className="text-sm text-gray-600 ml-2">({bestScore}/10)</span>
                                )}
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => toggleStar(word)}
                                  className={`p-2 rounded-lg transition ${
                                    word.starred
                                      ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                                      : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                                  }`}
                                  title={word.starred ? "Priority word (3x more likely)" : "Mark as priority"}
                                >
                                  <Star size={18} fill={word.starred ? "currentColor" : "none"} />
                                </button>
                                <button
                                  onClick={() => removeFromPracticeList(word)}
                                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                                  title="Remove from list"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <button
                      onClick={startReview}
                      disabled={practiceList.length === 0}
                      className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      Start Review Session <ArrowRight size={22} />
                    </button>
                  </div>
                )}

                {reviewTab === 'history' && (
                  <div>
                    {history.filter(h => !h.isReview).length > 0 && (
                      <div className="mb-4 flex items-center justify-end gap-3">
                        <Filter size={18} className="text-gray-600" />
                        <select
                          value={levelFilter}
                          onChange={(e) => setLevelFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="all">All Levels</option>
                          {availableLevels.map(level => (
                            <option key={level} value={level}>{LEVEL_DISPLAY[level]}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedHistoryWords.size > 0 && (
                      <div className="mb-4 p-4 bg-indigo-50 rounded-lg flex items-center justify-between">
                        <span className="text-indigo-900 font-semibold">
                          {selectedHistoryWords.size} word{selectedHistoryWords.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={addSelectedWordsToPracticeList}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition"
                        >
                          Add to Practice List
                        </button>
                      </div>
                    )}

                    {groupHistoryByDate().length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Calendar className="mx-auto mb-4 text-gray-400" size={48} />
                        <p>
                          {levelFilter === 'all'
                            ? 'No practice history yet!'
                            : `No ${LEVEL_DISPLAY[levelFilter]} words in history.`}
                        </p>
                        <p className="text-sm">
                          {levelFilter === 'all'
                            ? 'Complete your daily challenge to build your history.'
                            : 'Try a different filter.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {groupHistoryByDate().map(([date, words]) => (
                          <div key={date} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar size={18} className="text-gray-600" />
                                <span className="font-semibold text-gray-800">{date}</span>
                                <span className="text-sm text-gray-500">({words.length} word{words.length === 1 ? '' : 's'})</span>
                              </div>
                              <button
                                onClick={() => addDayToPracticeList(words)}
                                className="text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg hover:bg-indigo-200 transition font-medium"
                              >
                                Add All
                              </button>
                            </div>
                            <div className="p-2 space-y-1">
                              {words.map((h) => {
                                const alreadyInPractice = practiceList.some(w => w.word === h.word);
                                return (
                                  <div key={h.wordId} className={`flex items-center gap-3 p-2 rounded ${
                                    alreadyInPractice ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50'
                                  }`}>
                                    {!alreadyInPractice && (
                                      <input
                                        type="checkbox"
                                        checked={selectedHistoryWords.has(h.wordId)}
                                        onChange={() => toggleHistoryWord(h.wordId)}
                                        className="w-4 h-4 text-indigo-600 rounded flex-shrink-0"
                                      />
                                    )}
                                    {alreadyInPractice && <div className="w-4 h-4 flex-shrink-0" />}
                                    <span className={`font-medium truncate ${alreadyInPractice ? 'text-gray-500' : 'text-gray-700'}`}>
                                      {h.word}
                                    </span>
                                    <LevelBadge level={h.level} />
                                    {alreadyInPractice && (
                                      <span className="text-xs text-gray-500 ml-auto">In practice list</span>
                                    )}
                                    {!alreadyInPractice && (
                                      <span className="text-sm text-gray-500 ml-auto">{h.points}/10</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {reviewTab === 'upload' && (
                  <div>
                    <div className="mb-6 p-6 bg-blue-50 rounded-lg border border-blue-100">
                      <h3 className="font-semibold text-blue-900 mb-2">Upload Custom Words</h3>
                      <p className="text-sm text-gray-700 mb-4">
                        Upload a text file with one word per line. These words will be added to your practice list.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1 mb-4">
                        <li>• Plain text format (.txt)</li>
                        <li>• One word per line</li>
                        <li>• Duplicates will be automatically removed</li>
                      </ul>
                    </div>

                    <label className="block">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition">
                        <Upload className="mx-auto mb-4 text-gray-400" size={48} />
                        <p className="text-gray-700 font-semibold mb-1">Click to upload</p>
                        <p className="text-sm text-gray-500">or drag and drop</p>
                        <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}

            {settings && (
              <button
                onClick={() => {
                  setTempLanguage(settings.language);
                  setTempLevel(settings.level);

                  let currentSetting = settings.feedbackLanguage || 'auto';
                  if (currentSetting === 'auto') {
                    const isBeginner = settings.level === 'beginner';
                    currentSetting = isBeginner ? 'english' : 'target';
                  }
                  setTempFeedbackLanguage(currentSetting);
                  setShowSettings(true);
                }}
                className="w-full bg-white rounded-xl shadow-lg p-4 mt-6 text-gray-700 font-semibold hover:bg-gray-50 transition"
              >
                ⚙️ Settings
              </button>
            )}
          </div>

          {showSettings && (
            <>
              <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowSettings(false)} />
              <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <h2 className="text-2xl font-bold text-indigo-900 mb-6 text-center">Settings</h2>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Language:</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(LANGUAGES).map(([key, lang]) => (
                        <button
                          key={key}
                          onClick={() => setTempLanguage(key)}
                          className={`p-4 border-2 rounded-xl transition font-semibold ${
                            tempLanguage === key
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                              : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                          }`}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Level:</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {LEVELS.map(level => (
                        <button
                          key={level}
                          onClick={() => setTempLevel(level)}
                          className={`p-4 border-2 rounded-xl transition font-semibold ${
                            tempLevel === level
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                              : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                          }`}
                        >
                          {LEVEL_DISPLAY[level]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Feedback Language:</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setTempFeedbackLanguage('english')}
                        className={`p-4 border-2 rounded-xl transition font-semibold ${
                          tempFeedbackLanguage === 'english'
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                            : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                        }`}
                      >
                        English
                        {tempLevel === 'beginner' && (
                          <span className="block text-xs font-normal mt-1 text-indigo-600">(Recommended)</span>
                        )}
                      </button>
                      <button
                        onClick={() => setTempFeedbackLanguage('target')}
                        className={`p-4 border-2 rounded-xl transition font-semibold ${
                          tempFeedbackLanguage === 'target'
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                            : 'border-indigo-200 text-gray-700 hover:border-indigo-500 hover:bg-indigo-50'
                        }`}
                      >
                        {LANGUAGES[tempLanguage]?.name || 'Target Language'}
                        {['lower_intermediate', 'upper_intermediate', 'advanced'].includes(tempLevel) && (
                          <span className="block text-xs font-normal mt-1 text-indigo-600">(Recommended)</span>
                        )}
                      </button>
                    </div>
                  </div>

                  {availableCombos.length > 1 && (
                    <div className="mb-6 p-3 bg-blue-50 rounded-lg text-center">
                      <div className="text-sm text-blue-700">
                        You've tried {availableCombos.length} language/level combination{availableCombos.length === 1 ? '' : 's'}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowSettings(false)}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (tempLanguage && tempLevel) {
                          switchCombo(tempLanguage, tempLevel, tempFeedbackLanguage);
                          setShowSettings(false);
                        }
                      }}
                      disabled={!tempLanguage || !tempLevel}
                      className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'practice') {
    const currentWord = dailyWords[currentWordIndex];
    const languageCode = LANGUAGES[settings?.language]?.code || 'en';

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-2xl font-bold text-indigo-900">
                    Word {currentWordIndex + 1} of {dailyWords.length}
                  </h2>
                  <div className="text-sm text-gray-600">Attempt {attempts + 1}/3</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentWordIndex) / dailyWords.length) * 100}%` }}
                  />
                </div>
              </div>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6 border border-indigo-100">
                <div className={`font-bold text-indigo-900 mb-4 ${
                  currentWord.word.length > 20 ? 'text-2xl' :
                  currentWord.word.length > 15 ? 'text-3xl' : 'text-4xl'
                }`}>
                  {currentWord.word}
                </div>

                <div className="text-sm text-indigo-600 font-semibold mb-2">Example sentences:</div>
                {loading && examples.length === 0 ? (
                  <div className="text-gray-500 italic">Generating examples...</div>
                ) : (
                  <div className="space-y-2">
                    {examples.map((ex, i) => (
                      <div key={i} className="text-gray-700 pl-3 border-l-2 border-indigo-200">{ex}</div>
                    ))}
                    {extraExamplesCount < 2 && (
                      <button
                        onClick={requestMoreExamples}
                        disabled={loadingMoreExamples}
                        className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-semibold disabled:opacity-50"
                      >
                        {loadingMoreExamples ? 'Loading...' : (extraExamplesCount === 1 ? '+ More examples (1 remaining)' : '+ More examples')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {!feedback ? (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Write a sentence using '{currentWord.word}':
                      </label>
                      {!helpButtonUsed && settings?.level === 'beginner' && (
                        <button
                          onClick={fillHelpTemplate}
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition"
                          title="Fill template"
                        >
                          <HelpCircle size={16} />
                          <span>Need help starting?</span>
                        </button>
                      )}
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={userSentence}
                      onChange={(e) => setUserSentence(e.target.value)}
                      lang={languageCode}
                      className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-lg resize-none"
                      rows="3"
                      placeholder="Type your sentence here..."
                    />
                    {submitAttempted && userSentence.trim() && userSentence.trim().length < currentWord.word.length + 5 && (
                      <p className="text-sm text-red-600 mt-2">
                        Minimum {currentWord.word.length + 5} characters required (currently {userSentence.trim().length})
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={submitSentence}
                      disabled={loading || !userSentence.trim() || userSentence.trim().length < currentWord.word.length + 5}
                      className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Checking...' : 'Submit'}
                    </button>
                    <button
                      onClick={handleDontKnow}
                      disabled={loading}
                      className="flex-1 bg-amber-500 text-white py-3 rounded-lg font-semibold hover:bg-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      I Don't Know
                    </button>
                    <button
                      onClick={() => {
                        const updatedSettings = { ...settings, dailyWords, currentIndex: currentWordIndex };
                        setSettings(updatedSettings);
                        setScreen('home');
                      }}
                      disabled={loading}
                      className="bg-gray-300 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-400 transition disabled:opacity-50"
                    >
                      Exit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {showConfetti && <Confetti />}

                  <div className={`p-6 rounded-xl border-2 ${feedback.isError ? 'text-gray-700 bg-gray-50 border-gray-300' : getScoreColor(feedback.score)}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        {!feedback.isError && (
                          <>
                            <div className="text-3xl font-bold mb-1">{feedback.score}/10</div>
                            <div className="text-lg font-semibold">{getScoreLabel(feedback.score)}</div>
                          </>
                        )}
                      </div>
                      {attemptScores.length > 1 && (
                        <div className="text-sm text-gray-600">Best: {Math.max(...attemptScores)}/10</div>
                      )}
                    </div>

                    <div className="text-gray-700 mb-3">{feedback.message}</div>

                    {!showHaggleInput && feedback.translation && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <div className="text-sm font-semibold text-gray-600 mb-1">Translation:</div>
                        <div className="text-gray-800">{feedback.translation}</div>
                      </div>
                    )}

                    {showHaggleInput && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <div className="text-sm font-semibold text-gray-600 mb-1">Your sentence was:</div>
                        <div className="text-gray-800 italic">"{userSentence}"</div>
                      </div>
                    )}

                    {!showHaggleInput && canHaggleToday(settings.language) && !feedback.isError && (
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <button
                          onClick={handleHaggleClick}
                          className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold transition"
                        >
                          {haggleConversation.length > 0 ? '⚖️ Continue contesting' : '⚖️ Contest this score'}
                        </button>
                      </div>
                    )}

                    {showHaggleInput && (
                      <div className="mt-4 pt-4 border-t border-gray-300 space-y-3">
                        <div className="text-sm font-semibold text-gray-700">
                          Make your case (in {LANGUAGES[settings?.language]?.name}):
                        </div>
                        <textarea
                          value={haggleMessage}
                          onChange={(e) => setHaggleMessage(e.target.value)}
                          className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none text-sm resize-none"
                          rows="2"
                          placeholder="Explain why you deserve a higher score..."
                          disabled={haggleLoading}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={submitHaggle}
                            disabled={!haggleMessage.trim() || haggleLoading}
                            className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {haggleLoading ? 'Reviewing...' : 'Submit'}
                          </button>
                          <button
                            onClick={() => { setShowHaggleInput(false); setHaggleMessage(''); }}
                            disabled={haggleLoading}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    {attemptScores.length < 3 && (
                      <button
                        onClick={() => { setFeedback(null); setSubmitAttempted(false); }}
                        disabled={loading}
                        className="flex-1 bg-amber-500 text-white py-3 rounded-lg font-semibold hover:bg-amber-600 transition disabled:opacity-50"
                      >
                        Try Again ({attemptScores.length}/3)
                      </button>
                    )}
                    <button
                      onClick={() => completeWord()}
                      disabled={loading}
                      className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? 'Loading next word...' : (<>Continue <ArrowRight size={20} /></>)}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <div className="text-sm text-gray-600">
                  Today's score: <span className="font-bold text-indigo-600">{dailyPoints}/100</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
        {showHaggleConfirm && (
          <HaggleConfirmModal
            languageName={LANGUAGES[settings?.language]?.name}
            remaining={getHaggleRemaining(settings?.language)}
            onConfirm={() => { setShowHaggleConfirm(false); setShowHaggleInput(true); }}
            onCancel={() => setShowHaggleConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'review-practice') {
    const currentWord = reviewWords[reviewCurrentIndex];
    const languageCode = LANGUAGES[settings?.language]?.code || 'en';

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4 md:p-8">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-2xl font-bold text-amber-900">
                    Review: Word {reviewCurrentIndex + 1} of {reviewWords.length}
                  </h2>
                  <div className="text-sm text-gray-600">Attempt {attempts + 1}/3</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((reviewCurrentIndex) / reviewWords.length) * 100}%` }}
                  />
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-6 mb-6 border border-amber-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`font-bold text-amber-900 ${
                    currentWord.word.length > 20 ? 'text-2xl' :
                    currentWord.word.length > 15 ? 'text-3xl' : 'text-4xl'
                  }`}>
                    {currentWord.word}
                  </div>
                  <LevelBadge level={currentWord.level} />
                </div>

                <div className="text-sm text-amber-600 font-semibold mb-2">Example sentences:</div>
                {loading && examples.length === 0 ? (
                  <div className="text-gray-500 italic">Generating examples...</div>
                ) : (
                  <div className="space-y-2">
                    {examples.map((ex, i) => (
                      <div key={i} className="text-gray-700 pl-3 border-l-2 border-amber-200">{ex}</div>
                    ))}
                    {extraExamplesCount < 2 && (
                      <button
                        onClick={requestMoreExamples}
                        disabled={loadingMoreExamples}
                        className="mt-3 text-sm text-amber-600 hover:text-amber-800 font-semibold disabled:opacity-50"
                      >
                        {loadingMoreExamples ? 'Loading...' : (extraExamplesCount === 1 ? '+ Another example (1 remaining)' : '+ Another example')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {!feedback ? (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Write a sentence using '{currentWord.word}':
                      </label>
                      {!helpButtonUsed && settings?.level === 'beginner' && (
                        <button
                          onClick={fillHelpTemplate}
                          className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition"
                          title="Fill template"
                        >
                          <HelpCircle size={16} />
                          <span>Need help starting?</span>
                        </button>
                      )}
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={userSentence}
                      onChange={(e) => setUserSentence(e.target.value)}
                      lang={languageCode}
                      className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none text-lg resize-none"
                      rows="3"
                      placeholder="Type your sentence here..."
                    />
                    {submitAttempted && userSentence.trim() && userSentence.trim().length < currentWord.word.length + 5 && (
                      <p className="text-sm text-red-600 mt-2">
                        Minimum {currentWord.word.length + 5} characters required (currently {userSentence.trim().length})
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={submitSentence}
                      disabled={loading || !userSentence.trim() || userSentence.trim().length < currentWord.word.length + 5}
                      className="flex-1 bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Checking...' : 'Submit'}
                    </button>
                    <button
                      onClick={handleDontKnow}
                      disabled={loading}
                      className="flex-1 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      I Don't Know
                    </button>
                    <button
                      onClick={() => setShowExitConfirm(true)}
                      disabled={loading}
                      className="bg-gray-300 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-400 transition disabled:opacity-50"
                    >
                      Exit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {showConfetti && <Confetti />}

                  <div className={`p-6 rounded-xl border-2 ${feedback.isError ? 'text-gray-700 bg-gray-50 border-gray-300' : getScoreColor(feedback.score)}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        {!feedback.isError && (
                          <>
                            <div className="text-3xl font-bold mb-1">{feedback.score}/10</div>
                            <div className="text-lg font-semibold">{getScoreLabel(feedback.score)}</div>
                          </>
                        )}
                      </div>
                      {attemptScores.length > 1 && (
                        <div className="text-sm text-gray-600">Best: {Math.max(...attemptScores)}/10</div>
                      )}
                    </div>

                    <div className="text-gray-700 mb-3">{feedback.message}</div>

                    {!showHaggleInput && feedback.translation && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <div className="text-sm font-semibold text-gray-600 mb-1">Translation:</div>
                        <div className="text-gray-800">{feedback.translation}</div>
                      </div>
                    )}

                    {showHaggleInput && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <div className="text-sm font-semibold text-gray-600 mb-1">Your sentence was:</div>
                        <div className="text-gray-800 italic">"{userSentence}"</div>
                      </div>
                    )}

                    {!showHaggleInput && canHaggleToday(settings.language) && !feedback.isError && (
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <button
                          onClick={handleHaggleClick}
                          className="text-sm text-amber-600 hover:text-amber-800 font-semibold transition"
                        >
                          {haggleConversation.length > 0 ? '⚖️ Continue contesting' : '⚖️ Contest this score'}
                        </button>
                      </div>
                    )}

                    {showHaggleInput && (
                      <div className="mt-4 pt-4 border-t border-gray-300 space-y-3">
                        <div className="text-sm font-semibold text-gray-700">
                          Make your case (in {LANGUAGES[settings?.language]?.name}):
                        </div>
                        <textarea
                          value={haggleMessage}
                          onChange={(e) => setHaggleMessage(e.target.value)}
                          className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none text-sm resize-none"
                          rows="2"
                          placeholder="Explain why you deserve a higher score..."
                          disabled={haggleLoading}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={submitHaggle}
                            disabled={!haggleMessage.trim() || haggleLoading}
                            className="flex-1 bg-amber-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {haggleLoading ? 'Reviewing...' : 'Submit Appeal'}
                          </button>
                          <button
                            onClick={() => { setShowHaggleInput(false); setHaggleMessage(''); }}
                            disabled={haggleLoading}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    {attemptScores.length < 3 && (
                      <button
                        onClick={() => { setFeedback(null); setSubmitAttempted(false); }}
                        disabled={loading}
                        className="flex-1 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50"
                      >
                        Try Again ({attemptScores.length}/3)
                      </button>
                    )}
                    <button
                      onClick={() => completeReviewWord()}
                      disabled={loading}
                      className="flex-1 bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? 'Loading next word...' : (<>Continue <ArrowRight size={20} /></>)}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <div className="text-sm text-gray-600">
                  Session score: <span className="font-bold text-amber-600">{reviewTotalPoints}/100</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
        {showHaggleConfirm && (
          <HaggleConfirmModal
            languageName={LANGUAGES[settings?.language]?.name}
            remaining={getHaggleRemaining(settings?.language)}
            onConfirm={() => { setShowHaggleConfirm(false); setShowHaggleInput(true); }}
            onCancel={() => setShowHaggleConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'complete') {
    const sessionWords = history.slice(-dailyWords.length);
    const sessionPoints = sessionWords.reduce((sum, w) => sum + w.points, 0);
    const perfectScore = sessionWords.every(w => w.points === 10);
    const canLevelUp = settings.level !== 'advanced' && perfectScore;

    const updatedSettings = { ...settings, currentIndex: 0 };
    if (settings.currentIndex !== 0) {
      setSettings(updatedSettings);
    }

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h1 className="text-3xl md:text-4xl font-bold text-indigo-900 mb-4">Great Work Today!</h1>
              <p className="text-xl text-gray-700 mb-6">You've completed your daily challenge</p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <div className="text-3xl font-bold text-indigo-900 mb-2">{sessionPoints}/100</div>
                <div className="text-gray-600">Today's score</div>
              </div>

              {canLevelUp && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="text-lg font-semibold text-green-900 mb-2">🌟 Perfect score!</div>
                  <p className="text-sm text-green-700">
                    You got 10/10 on every word! Consider trying a higher level for more challenge!
                  </p>
                </div>
              )}

              {currentStreak > 1 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
                  <div className="text-lg font-semibold text-orange-900 mb-2">🔥 {currentStreak} day streak!</div>
                  <p className="text-sm text-orange-700">
                    {currentStreak >= 7
                      ? `Amazing! You're on fire! Keep it up!`
                      : currentStreak >= 3
                      ? `Great consistency! Keep it going!`
                      : `You're building momentum!`}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                  <div className="text-2xl font-bold text-green-900">
                    {sessionWords.filter(w => w.known).length}
                  </div>
                  <div className="text-sm text-green-700">Words learned</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <div className="text-2xl font-bold text-purple-900">{totalPoints}</div>
                  <div className="text-sm text-purple-700">Total score</div>
                </div>
              </div>

              <button
                onClick={() => setScreen('home')}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
      </>
    );
  }

  if (screen === 'review-complete') {
    const sessionWords = history.filter(h => h.isReview).slice(-reviewWords.length);
    const sessionPoints = sessionWords.reduce((sum, w) => sum + w.points, 0);

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4 md:p-8">
          <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
            <img src="/logo.svg" alt="SqueakEasy" className="w-10 h-10 opacity-80" />
            {currentStreak > 0 && (
              <div className="bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1">
                <span className="text-lg">🔥</span>
                <span className="font-bold text-gray-800">{currentStreak}</span>
              </div>
            )}
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h1 className="text-3xl font-bold text-amber-900 mb-4">Review Session Complete!</h1>
              <p className="text-xl text-gray-700 mb-6">You reviewed {reviewWords.length} words</p>

              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <div className="text-3xl font-bold text-amber-900 mb-2">{sessionPoints}/100</div>
                <div className="text-gray-600">Session score</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                  <div className="text-2xl font-bold text-green-900">
                    {sessionWords.filter(w => w.known).length}
                  </div>
                  <div className="text-sm text-green-700">Words mastered</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                  <div className="text-2xl font-bold text-orange-900">{reviewTotalPoints}</div>
                  <div className="text-sm text-orange-700">Total review score</div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => { setMainTab('review'); setScreen('home'); }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Back to Review Centre
                </button>
                <button
                  onClick={startReview}
                  className="flex-1 bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 transition"
                >
                  Continue (10 more)
                </button>
              </div>
            </div>
          </div>
        </div>

        {toast && <Toast message={toast.message || toast} onClose={() => setToast(null)} onUndo={toast?.onUndo} />}
        {showExitConfirm && (
          <ConfirmModal
            message="Exit review session? Progress will not be saved."
            onConfirm={() => { setShowExitConfirm(false); setMainTab('review'); setScreen('home'); }}
            onCancel={() => setShowExitConfirm(false)}
          />
        )}
      </>
    );
  }
};

export default VocabLearner;